import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRole } from '~/shared/data-contract/job-role';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { updateLevelReviewerData, updateRoleReviewerData } from './cache';
import { jobArchitectureKeys } from './query-keys';

type JobItem = JobLevel | JobLevelDetails | JobRole | JobRoleDetails;

export type UseUnassignReviewerResult = UseMutationResult<JobLevel | JobRole, Error, JobItem>;

export const useUnassignReviewer = (): UseUnassignReviewerResult => {
    const { unassignReviewer } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (item: JobItem) => {
            const result = await unassignReviewer({ item });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async (jobItem) => {
            if (jobItem.type === 'job-role') {
                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.rolesByCluster(jobItem.architectureId, jobItem.parentId ?? ''),
                });

                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.role(jobItem.architectureId, jobItem.id),
                });

                updateRoleReviewerData({ ...jobItem, reviewer: undefined }, queryClient);
            }

            if (jobItem.type === 'job-level' && jobItem.reviewer !== undefined) {
                await queryClient.cancelQueries({
                    queryKey: jobArchitectureKeys.levelsByRole(jobItem.architectureId, jobItem.parentId ?? ''),
                });

                updateLevelReviewerData({ ...jobItem, reviewer: undefined }, queryClient);
            }
        },
        onSuccess: (_, jobItem) => {
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architecture(jobItem.architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.architectureDetail(jobItem.architectureId),
            });
            queryClient.invalidateQueries({
                queryKey: jobArchitectureKeys.jobProfileManagement(),
            });
        },
    });
};
